/**
 * Typed JS wrapper around the BroadcastBridge Capacitor plugin
 * (ios/App/App/BroadcastBridge/BroadcastBridge.swift).
 *
 * The native side writes JPEG frames into an App Group container from the
 * BroadcastUploadExtension. This wrapper lets the React UI list those frames,
 * consume them (read + delete in one shot), check broadcast status, and
 * clear the directory.
 *
 * Web fallback: every method short-circuits to an empty result. This lets
 * the React code call broadcastBridge.* unconditionally without branching
 * on platform.
 *
 * Plugin registration: capacitor.config.ts has a `BroadcastBridge: {}`
 * entry. After the user runs `npx cap sync ios`, Capacitor links the
 * native plugin to the JS proxy below.
 */
import { Capacitor, registerPlugin } from "@capacitor/core";

export type BroadcastState = "idle" | "recording" | "paused" | "finished";

export interface BroadcastStatus {
  state: BroadcastState;
  /** Unix seconds. Present on `recording` and after. */
  startedAt?: number;
  /** Unix seconds. Only present on `finished`. */
  endedAt?: number;
  /**
   * iOS's own answer: `UIScreen.isCaptured` at the moment of the call (true
   * while any broadcast, recording or mirroring captures the screen). Absent
   * on binaries before 2026-09-24.
   */
  captured?: boolean;
  /** Unix seconds: when the extension last wrote status.json. */
  updatedAt?: number;
}

export interface BroadcastFrameMeta {
  /** Absolute filesystem path inside the App Group container. */
  path: string;
  /** Unix milliseconds (the SampleHandler names files <unixMs>.jpg). */
  timestamp: number;
  sizeBytes: number;
}

export interface ConsumedFrame {
  base64: string;
  sizeBytes: number;
}

export interface BroadcastDebugEvent {
  event: string;
  timestamp?: string;
  unixSeconds?: number;
  [key: string]: unknown;
}

export interface BroadcastDebugLog {
  events: BroadcastDebugEvent[];
  appGroupAvailable?: boolean;
  fileExists?: boolean;
  parseError?: boolean;
  updatedAt?: number;
  appGroup?: string;
  extensionBundleId?: string;
}

export interface BroadcastDiagnostics {
  appGroup: string;
  appGroupAvailable: boolean;
  /** Host app bundle id reported by Bundle.main.bundleIdentifier. */
  hostBundleId?: string;
  containerPath?: string;
  statusFileExists?: boolean;
  status?: BroadcastStatus & Record<string, unknown>;
  debugFileExists?: boolean;
  debugEventCount?: number;
  debugLastEvents?: BroadcastDebugEvent[];
  debugUpdatedAt?: number;
  framesOnDisk?: number;
  framesTotalBytes?: number;
  legacyFrameExists?: boolean;
  /**
   * "native" — value came back from the native plugin.
   * "wrapper-web" — running on web (no native bridge at all).
   * "wrapper-plugin-missing" — native bridge is up but BroadcastBridge plugin
   *   is not registered (App target missing the Swift/.m files in pbxproj,
   *   or Capacitor never linked the plugin).
   * "wrapper-call-threw" — native call threw; captured exception in `error`.
   * Without this field the fallback shape and the native "appGroup unavailable"
   * shape are indistinguishable on device.
   */
  source?: "native" | "wrapper-web" | "wrapper-plugin-missing" | "wrapper-call-threw";
  error?: string;
}

export interface BroadcastBridgePlugin {
  getStatus(): Promise<BroadcastStatus>;
  listFrames(): Promise<{ frames: BroadcastFrameMeta[] }>;
  consumeFrame(opts: { path: string }): Promise<ConsumedFrame>;
  clearFrames(): Promise<{ deleted: number }>;
  getDebug(): Promise<BroadcastDebugLog>;
  getDiagnostics(): Promise<BroadcastDiagnostics>;
}

// Capacitor falls back to a JS stub when the plugin is missing (web build,
// dev preview without `cap sync`). We wrap the proxy so callers see a
// stable, sensible default rather than a runtime throw.
const native = registerPlugin<BroadcastBridgePlugin>("BroadcastBridge");

function isAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("BroadcastBridge");
}

/**
 * Read the current broadcast status. Safe to call repeatedly (cheap file
 * read). Returns `{ state: "idle" }` on web and when the extension has
 * not yet written status.json.
 */
export async function getBroadcastStatus(): Promise<BroadcastStatus> {
  if (!isAvailable()) return { state: "idle" };
  try {
    return await native.getStatus();
  } catch {
    return { state: "idle" };
  }
}

/**
 * Is a system broadcast ACTUALLY live right now, according to iOS?
 *
 * This is the authoritative answer, and it exists because the scan's own JS
 * phase is NOT one: the phase drifts (a component remount resets it to idle)
 * and drifting broke stop() in both directions —
 *   - phase drifted to idle while iOS was still recording → the real stop was
 *     skipped and the broadcast was orphaned (red bar stuck on);
 *   - phase stale at "scanning" while nothing was live → stopBroadcast ran and
 *     PRESENTED the iOS picker, the "every page link opens the Start Broadcast
 *     sheet" bug.
 *
 * Returns `null` when we genuinely cannot know (no native bridge, e.g. web, or
 * the call threw) so callers can fall back to their own heuristic instead of
 * mistaking "unknown" for "not broadcasting".
 */
export async function isBroadcastLive(): Promise<boolean | null> {
  if (!isAvailable()) return null;
  try {
    const status = await native.getStatus();
    // status.json is only as fresh as the extension's last write. An extension
    // iOS killed (memory) never writes "finished", so its file says
    // "recording" forever; the screen is no longer captured, though, and that
    // flag comes from iOS itself (2026-09-24).
    if (status.captured === false) return false;
    return status.state === "recording" || status.state === "paused";
  } catch {
    return null;
  }
}

/**
 * List frames currently waiting in the App Group container. Oldest first
 * so the upload loop drains in capture order.
 */
export async function listBroadcastFrames(): Promise<BroadcastFrameMeta[]> {
  if (!isAvailable()) return [];
  try {
    const res = await native.listFrames();
    return res?.frames ?? [];
  } catch {
    return [];
  }
}

/**
 * Read a frame's JPEG bytes as base64, then delete the file. The deletion
 * is the "consume" half: never call consumeFrame twice for the same path,
 * the second call will reject because the file is gone.
 */
export async function consumeBroadcastFrame(path: string): Promise<ConsumedFrame | null> {
  if (!isAvailable()) return null;
  try {
    return await native.consumeFrame({ path });
  } catch {
    return null;
  }
}

/**
 * Wipe every frame in /frames/. Used when the trainer dismisses the pill
 * mid-broadcast or when the upload loop hits an unrecoverable error.
 * Returns the number of frames deleted (0 on web).
 */
export async function clearBroadcastFrames(): Promise<number> {
  if (!isAvailable()) return 0;
  try {
    const res = await native.clearFrames();
    return res?.deleted ?? 0;
  } catch {
    return 0;
  }
}

/**
 * Read the SampleHandler's structured telemetry log (debug.json) from the
 * App Group container. Use this to confirm the Broadcast Upload Extension
 * actually launched and to see lifecycle / frame-write events. Safe on web
 * (returns an empty log).
 */
export async function getBroadcastDebug(): Promise<BroadcastDebugLog> {
  if (!isAvailable()) return { events: [], appGroupAvailable: false };
  try {
    return await native.getDebug();
  } catch {
    return { events: [] };
  }
}

/**
 * One-shot diagnostic snapshot for the in-app debug surface. Bundles App
 * Group availability, status.json, debug.json summary, and on-disk frame
 * count so Sam can read the broadcast pipeline state without Xcode. Safe
 * on web (returns appGroupAvailable: false).
 */
export async function getBroadcastDiagnostics(): Promise<BroadcastDiagnostics> {
  const appGroup = "group.com.nexusdex.app.broadcast";
  if (!Capacitor.isNativePlatform()) {
    return { appGroup, appGroupAvailable: false, source: "wrapper-web" };
  }
  if (!Capacitor.isPluginAvailable("BroadcastBridge")) {
    return { appGroup, appGroupAvailable: false, source: "wrapper-plugin-missing" };
  }
  try {
    const res = await native.getDiagnostics();
    return { ...res, source: "native" };
  } catch (e) {
    return {
      appGroup,
      appGroupAvailable: false,
      source: "wrapper-call-threw",
      error: e instanceof Error ? `${e.name}: ${e.message}` : String(e),
    };
  }
}

export const broadcastBridge = {
  isAvailable,
  getStatus: getBroadcastStatus,
  listFrames: listBroadcastFrames,
  consumeFrame: consumeBroadcastFrame,
  clearFrames: clearBroadcastFrames,
  getDebug: getBroadcastDebug,
  getDiagnostics: getBroadcastDiagnostics,
};
